/**
 * The weights of a track's criteria, on the track's Criteria & Scales tab
 * (migration 1047; canon 02 §2A: criteria and their weights live in the Criteria
 * stage).
 *
 * Each criterion's weight is its share of the track's balance. Sessions on the
 * track start from these weights when every criterion has one, and from an even
 * split otherwise. Saved through `saveTrackCriteriaWeights` — the save the cycle
 * configure wizard's Weights step makes. Drawn as fields only for someone who may
 * change the track's criteria while they are open (build contract §2.8); the
 * database holds the same two rules.
 */
import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  useAttributeGovernanceStore,
  type CycleAttributeConfig,
} from '../../../stores/prioritization/attributeGovernanceStore';
import { checkTrackWeights, formatWeight, type TrackWeights } from '../../../lib/trackCriteriaWeights';
import { isObjectiveCriterionKey } from '@/lib/objectiveCriteria';

export interface TrackCriteriaWeightsProps {
  trackId: string;
  /** The track's criteria rows, in track order. */
  rows: readonly CycleAttributeConfig[];
  labelOf: (attributeKey: string) => string;
  /** The track's criteria are locked. */
  locked: boolean;
  /** The person may change the track's criteria; when not, why not. */
  gate: { allowed: boolean; reason?: string };
  /** Called after a save, to read the rows back. */
  onSaved?: () => void | Promise<void>;
}

const savedWeights = (rows: readonly CycleAttributeConfig[]): TrackWeights =>
  Object.fromEntries(rows.map((r) => [r.attributeKey, r.weight ?? null]));

const draftFrom = (weights: TrackWeights): Record<string, string> =>
  Object.fromEntries(Object.entries(weights).map(([k, w]) => [k, w == null ? '' : formatWeight(w)]));

function parse(draft: Record<string, string>): TrackWeights {
  return Object.fromEntries(
    Object.entries(draft).map(([k, v]) => [k, v.trim() === '' ? null : Number(v)]),
  );
}

export function TrackCriteriaWeights({ trackId, rows, labelOf, locked, gate, onSaved }: TrackCriteriaWeightsProps) {
  const saveTrackCriteriaWeights = useAttributeGovernanceStore((s) => s.saveTrackCriteriaWeights);
  const saved = useMemo(() => savedWeights(rows), [rows]);
  const savedKey = JSON.stringify(saved);
  const [draft, setDraft] = useState<Record<string, string>>(() => draftFrom(saved));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // What is saved changed (read back, or another screen): start from it again.
  useEffect(() => {
    setDraft(draftFrom(JSON.parse(savedKey) as TrackWeights));
  }, [savedKey]);

  if (rows.length === 0) return null;

  const editable = !locked && gate.allowed;
  const savedCheck = checkTrackWeights(saved);
  const weights = parse(draft);
  const check = checkTrackWeights(weights);
  const changed = JSON.stringify(weights) !== savedKey;
  // Strategic objectives (1066) read as one share of the balance, split among them.
  const objectiveKeys = rows.map((r) => r.attributeKey).filter(isObjectiveCriterionKey);
  const objectiveWeights = objectiveKeys.map((k) => weights[k]);
  const strategicFit = objectiveKeys.length >= 2 && objectiveWeights.every((w) => w != null && Number.isFinite(w))
    ? objectiveWeights.reduce<number>((sum, w) => sum + (w ?? 0), 0)
    : null;

  const save = async (next: TrackWeights) => {
    setBusy(true);
    setError(null);
    try {
      const { error: refused } = await saveTrackCriteriaWeights(trackId, next);
      if (refused) {
        setError(`Not saved: ${refused}`);
        return;
      }
      await onSaved?.();
    } finally {
      setBusy(false);
    }
  };

  const effect = savedCheck.allSet && savedCheck.ok
    ? 'Sessions on this track start from these weights.'
    : savedCheck.allSet
      ? 'Sessions on this track start from these weights, scaled to 100.'
      : 'No weights set: sessions on this track start with an even split.';

  return (
    <section aria-label="Weights" className="border border-[var(--rule)] p-4 space-y-3" data-testid="track-criteria-weights">
      <div>
        <p className="font-mono text-[0.55rem] uppercase tracking-[0.14em] text-[var(--text-dim)]">Weights</p>
        <p className="text-sm text-[var(--text-sec)] mt-1">{effect}</p>
        {locked && (
          <p className="text-xs text-[var(--text-ter)] mt-1">Locked with the criteria. Reopen them to change a weight.</p>
        )}
        {!locked && !gate.allowed && gate.reason && (
          <p className="text-xs text-[var(--text-ter)] mt-1">{gate.reason}</p>
        )}
      </div>

      <ul className="space-y-1.5">
        {rows.map((r) => (
          <li key={r.id} className="flex items-center justify-between gap-3 text-sm">
            <span className="text-[var(--text-pri)]">{labelOf(r.attributeKey)}</span>
            {editable ? (
              <label className="flex items-center gap-1.5 text-[var(--text-sec)]">
                <input
                  aria-label={`Weight for ${labelOf(r.attributeKey)}`}
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  inputMode="decimal"
                  value={draft[r.attributeKey] ?? ''}
                  onChange={(e) => setDraft((d) => ({ ...d, [r.attributeKey]: e.target.value }))}
                  className="w-20 px-2 py-1 border border-[var(--rule)] bg-[var(--bg)] text-right"
                />
                %
              </label>
            ) : (
              <span className="text-[var(--text-sec)]">{r.weight == null ? '—' : `${formatWeight(r.weight)}%`}</span>
            )}
          </li>
        ))}
      </ul>

      {strategicFit != null && (
        <p className="text-xs text-[var(--text-sec)]" data-testid="strategic-fit-share">
          Strategic fit, the {objectiveKeys.length} objectives together: {formatWeight(strategicFit)}% of the balance.
        </p>
      )}

      {editable && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--rule)] pt-3">
          <span className={`text-sm ${check.ok ? 'text-[var(--text-sec)]' : 'text-[var(--mustard)]'}`} role="status">
            {check.noneSet ? 'No weights: an even split' : `Total ${formatWeight(check.total)} of 100`}
            {!check.ok && check.error ? ` — ${check.error}` : ''}
          </span>
          <div className="flex items-center gap-2">
            {!savedCheck.noneSet && (
              <button
                type="button"
                disabled={busy}
                onClick={() => void save(Object.fromEntries(rows.map((r) => [r.attributeKey, null])))}
                className="px-3 py-1.5 text-sm text-[var(--text-sec)] hover:bg-[var(--hover-tint)] disabled:opacity-50"
              >
                Clear weights
              </button>
            )}
            <button
              type="button"
              disabled={busy || !changed || !check.ok}
              onClick={() => void save(weights)}
              className="btn btn-primary text-sm flex items-center gap-2 disabled:opacity-50"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              Save weights
            </button>
          </div>
        </div>
      )}
      {error && <p className="text-sm text-[var(--coral)]">{error}</p>}
    </section>
  );
}

export default TrackCriteriaWeights;
